import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { createReport } from "@/lib/karma/storage";
import { getSessionUser } from "@/lib/supabase/auth";
import { getSupabaseClient, getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl, resilientFetch } from "@/lib/supabase/client";

export type SocialVisibility = "public" | "friends" | "private";
export type SocialPostTab = "posts" | "reposts" | "tagged" | "saved";

export type SocialMediaItem = {
  id: string;
  type: "image" | "video";
  url: string;
};

export type SocialProfileCard = {
  id: string;
  displayName: string;
  handle: string;
  photoUrl: string | null;
  bio: string | null;
  interests: string[];
  destinyNumber: number | null;
  lifePathNumber: number | null;
  expressionNumber: number | null;
  matchScore: number;
  commonInterests: number;
  followedByMe: boolean;
  followsMe: boolean;
  isFriend: boolean;
  isBlocked: boolean;
  isMuted: boolean;
};

export type SocialFeedPost = {
  id: string;
  authorUserId: string;
  authorName: string;
  authorHandle: string;
  authorPhotoUrl: string | null;
  body: string;
  tags: string[];
  location: string | null;
  taggedHandles: string[];
  media: SocialMediaItem[];
  createdAt: string;
  pinned: boolean;
  isRepost: boolean;
  repostOfPostId: string | null;
  postType: "standard" | "affirmation";
  affirmationStyle: "violet" | "rose";
  likeCount: number;
  commentCount: number;
  saveCount: number;
  repostCount: number;
  viewCount: number;
  interactionCount: number;
  collectiveEnergyPercent: number;
  likedByMe: boolean;
  savedByMe: boolean;
  repostedByMe: boolean;
  reactions: Record<string, string[]>;
  comments: Array<{
    id: string;
    userId: string;
    userName: string;
    body: string;
    createdAt: string;
  }>;
};

export type SocialActivityKind = "meditasyon" | "nefes" | "diyet" | "rituel" | "sohbet" | "ozel";

export type SocialActivityItem = {
  id: string;
  title: string;
  subtitle: string;
  createdAt: string;
  taskKind: SocialActivityKind;
  taskPoints: number | null;
  streakDays: number;
  authorUserId: string;
  authorName: string;
  authorHandle: string;
  authorPhotoUrl: string | null;
};

export type BlockedUserItem = {
  id: string;
  displayName: string;
  handle: string;
  photoUrl: string | null;
  blockedAt: string | null;
};

type LocalPost = {
  id: string;
  authorUserId: string;
  body: string;
  tags: string[];
  location: string | null;
  taggedHandles: string[];
  media: SocialMediaItem[];
  createdAt: string;
  pinned: boolean;
  isRepost: boolean;
  repostOfPostId: string | null;
  postType: "standard" | "affirmation";
  affirmationStyle: "violet" | "rose";
  likedUserIds: string[];
  savedUserIds: string[];
  repostedUserIds: string[];
  seenUserIds: string[];
  reactions: Record<string, string[]>;
  comments: Array<{ id: string; userId: string; body: string; createdAt: string }>;
};

const SOCIAL_POSTS_KEY = "panel_social_posts_v2";
const SOCIAL_MUTES_KEY = "panel_social_mutes_v1";
const SOCIAL_STATUS_KEY = "panel_social_status_v1";

type SocialAuthHeaders = {
  "Content-Type": string;
  Authorization: string;
};

async function getSocialAuthHeaders(): Promise<SocialAuthHeaders | null> {
  const parseToken = (raw: string) => {
    if (!raw) return "";
    const direct = raw.trim();
    if (direct && !direct.startsWith("{")) return direct;

    try {
      const parsed = JSON.parse(raw) as any;
      return (
        (typeof parsed?.access_token === "string" ? parsed.access_token : "") ||
        (typeof parsed?.currentSession?.access_token === "string" ? parsed.currentSession.access_token : "") ||
        (typeof parsed?.session?.access_token === "string" ? parsed.session.access_token : "") ||
        ""
      );
    } catch {
      return "";
    }
  };

  let token = "";

  try {
    const supabase = getSupabaseClient();
    const { data } = await supabase.auth.getSession();
    token = String(data.session?.access_token ?? "").trim();
  } catch {
    token = "";
  }

  if (!token) {
    try {
      const { directUrl } = getSupabaseConfig();
      const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
      if (storageKey) {
        token = parseToken(globalThis.localStorage?.getItem(storageKey) ?? "");
      }
      if (!token) {
        token = parseToken(globalThis.localStorage?.getItem("supabase.auth.token") ?? "");
      }
    } catch {
      token = "";
    }
  }

  if (!token) return null;

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function socialApiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getSocialAuthHeaders();
  if (!headers) throw new Error("Oturum yok");

  const mergedHeaders = new Headers(init?.headers ?? undefined);
  mergedHeaders.set("Content-Type", headers["Content-Type"]);
  mergedHeaders.set("Authorization", headers.Authorization);

  const response = await resilientFetch(path, {
    ...init,
    headers: mergedHeaders,
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } & T;
  if (!response.ok) {
    throw new Error(json?.error || "Akış işlemi başarısız oldu");
  }

  return json;
}

function normalizeHandleLike(value: string) {
  return String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z_]+/g, "");
}

function formatDisplayNameFromHandle(value: string) {
  return String(value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toLocaleUpperCase("tr-TR"));
}

function normalizeDisplayName(row: any, options?: { fallbackDisplayName?: string }) {
  const displayName = typeof row?.display_name === "string" ? row.display_name.trim() : "";
  const handle = typeof row?.handle === "string" ? row.handle.trim() : "";

  const genericDisplayNames = new Set(["", "kullanıcı", "kullanici", "user", "uye", "üye", "biri"]);
  const isGeneric = genericDisplayNames.has(displayName.toLocaleLowerCase("tr-TR").trim());

  const normalizedDisplay = normalizeHandleLike(displayName);
  const normalizedHandle = normalizeHandleLike(handle);
  const isHandleLike = Boolean(normalizedDisplay && normalizedHandle && normalizedDisplay === normalizedHandle);

  if (displayName && !isGeneric && !isHandleLike) return displayName;

  const fallbackDisplayName = String(options?.fallbackDisplayName ?? "").trim();
  const normalizedFallback = normalizeHandleLike(fallbackDisplayName);
  if (fallbackDisplayName && (!normalizedHandle || normalizedFallback !== normalizedHandle)) {
    return fallbackDisplayName;
  }

  if (handle) return formatDisplayNameFromHandle(handle);
  return "Kullanıcı";
}

function parseBirthDateFromIso(value: unknown) {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return null;

  const isoDate = /^\d{4}-\d{2}-\d{2}/.exec(text)?.[0] ?? "";
  const normalized = isoDate || text;
  const [year, month, day] = normalized.split("-").map((n) => Number(n));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return parseBirthDate({ day, month, year });
}

function parseInterests(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((it) => String(it ?? "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith("[") && text.endsWith("]")) {
      try {
        const arr = JSON.parse(text);
        if (Array.isArray(arr)) return arr.map((it) => String(it ?? "").trim()).filter(Boolean);
      } catch {
        // ignore
      }
    }
    return text
      .split(",")
      .map((it) => it.trim())
      .filter(Boolean);
  }

  return [];
}

function parseTaggedHandles(value: unknown) {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((it) => String(it ?? "").trim().replace(/^@+/, "").toLocaleLowerCase("tr-TR"))
          .filter(Boolean),
      ),
    );
  }

  if (typeof value === "string") {
    return Array.from(
      new Set(
        value
          .split(",")
          .map((it) => it.trim().replace(/^@+/, "").toLocaleLowerCase("tr-TR"))
          .filter(Boolean),
      ),
    );
  }

  return [];
}

function inferMediaTypeFromUrl(url: string): "image" | "video" {
  const value = url.toLocaleLowerCase("tr-TR");
  if (value.includes(".mp4") || value.includes(".mov") || value.includes(".webm") || value.includes("video")) {
    return "video";
  }
  return "image";
}

function normalizeMedia(value: unknown): SocialMediaItem[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((it) => {
      const url = typeof (it as any)?.url === "string" ? String((it as any).url).trim() : "";
      if (!url) return null;
      const providedType = (it as any)?.type === "video" ? "video" : (it as any)?.type === "image" ? "image" : null;
      return {
        id: typeof (it as any)?.id === "string" ? (it as any).id : makeId("media"),
        type: providedType ?? inferMediaTypeFromUrl(url),
        url,
      } satisfies SocialMediaItem;
    })
    .filter(Boolean) as SocialMediaItem[];
}

function extractHashtags(text: string) {
  const parts = String(text ?? "").match(/#[^\s#.,!?;:()\[\]{}]+/g) ?? [];
  return Array.from(new Set(parts.map((part) => part.toLocaleLowerCase("tr-TR"))));
}

function getNowIso() {
  return new Date().toISOString();
}

function makeId(prefix: string) {
  try {
    const id = globalThis.crypto?.randomUUID?.();
    if (id) return `${prefix}_${id}`;
  } catch {
    // ignore
  }
  return `${prefix}_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function readLocalJson<T>(key: string, fallback: T): T {
  try {
    const raw = globalThis.localStorage?.getItem(key) ?? "";
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeLocalJson<T>(key: string, value: T) {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function migrateLegacyPosts(posts: any[]): LocalPost[] {
  return (posts ?? [])
    .map((post) => {
      const body = String(post?.body ?? "").trim();
      if (!body && !Array.isArray(post?.media)) return null;

      const media = normalizeMedia(post?.media);
      const tags = Array.isArray(post?.tags) ? post.tags.map((tag: any) => String(tag ?? "").trim()).filter(Boolean) : extractHashtags(body);

      const postType = post?.postType === "affirmation" || tags.includes("olumlama") ? "affirmation" : "standard";
      const affirmationStyle = post?.affirmationStyle === "rose" ? "rose" : "violet";

      return {
        id: typeof post?.id === "string" ? post.id : makeId("post"),
        authorUserId: String(post?.authorUserId ?? ""),
        body,
        tags,
        location: typeof post?.location === "string" ? post.location.trim() : null,
        taggedHandles: parseTaggedHandles(post?.taggedHandles),
        media,
        createdAt: typeof post?.createdAt === "string" ? post.createdAt : getNowIso(),
        pinned: Boolean(post?.pinned),
        isRepost: Boolean(post?.isRepost),
        repostOfPostId: typeof post?.repostOfPostId === "string" ? post.repostOfPostId : null,
        postType,
        affirmationStyle,
        likedUserIds: Array.isArray(post?.likedUserIds)
          ? post.likedUserIds.map((id: any) => String(id ?? "")).filter(Boolean)
          : Array.isArray(post?.reactions?.destek)
            ? post.reactions.destek.map((id: any) => String(id ?? "")).filter(Boolean)
            : [],
        savedUserIds: Array.isArray(post?.savedUserIds) ? post.savedUserIds.map((id: any) => String(id ?? "")).filter(Boolean) : [],
        repostedUserIds: Array.isArray(post?.repostedUserIds) ? post.repostedUserIds.map((id: any) => String(id ?? "")).filter(Boolean) : [],
        seenUserIds: Array.isArray(post?.seenUserIds) ? post.seenUserIds.map((id: any) => String(id ?? "")).filter(Boolean) : [],
        reactions: typeof post?.reactions === "object" && post?.reactions ? (post.reactions as Record<string, string[]>) : {},
        comments: Array.isArray(post?.comments)
          ? post.comments
              .map((comment: any) => {
                const userId = String(comment?.userId ?? "");
                const bodyText = String(comment?.body ?? "").trim();
                if (!userId || !bodyText) return null;
                return {
                  id: typeof comment?.id === "string" ? comment.id : makeId("comment"),
                  userId,
                  body: bodyText,
                  createdAt: typeof comment?.createdAt === "string" ? comment.createdAt : getNowIso(),
                };
              })
              .filter(Boolean) as Array<{ id: string; userId: string; body: string; createdAt: string }>
          : [],
      } satisfies LocalPost;
    })
    .filter(Boolean) as LocalPost[];
}

function readLocalPosts() {
  const legacy = readLocalJson<any[]>(SOCIAL_POSTS_KEY, []);
  return migrateLegacyPosts(legacy);
}

function saveLocalPosts(posts: LocalPost[]) {
  writeLocalJson(SOCIAL_POSTS_KEY, posts);
}

function readLocalMutes() {
  return readLocalJson<Record<string, string[]>>(SOCIAL_MUTES_KEY, {});
}

function saveLocalMutes(map: Record<string, string[]>) {
  writeLocalJson(SOCIAL_MUTES_KEY, map);
}

export async function getMySocialHeaderInDb() {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const metadata = (sessionUser.metadata ?? {}) as Record<string, unknown>;

  const normalizeHandle = (value: string) =>
    String(value ?? "")
      .toLocaleLowerCase("tr-TR")
      .trim()
      .replace(/\s+/g, "")
      .replace(/[ç]/g, "c")
      .replace(/[ğ]/g, "g")
      .replace(/[ı]/g, "i")
      .replace(/[ö]/g, "o")
      .replace(/[ş]/g, "s")
      .replace(/[ü]/g, "u")
      .replace(/[^0-9a-z_]+/g, "")
      .slice(0, 24);

  const pickMetadataDisplayName = (meta: Record<string, unknown>) => {
    const first = typeof meta.first_name === "string" ? meta.first_name.trim() : typeof meta.firstName === "string" ? meta.firstName.trim() : "";
    const last = typeof meta.last_name === "string" ? meta.last_name.trim() : typeof meta.lastName === "string" ? meta.lastName.trim() : "";
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;

    const fullName = typeof meta.full_name === "string" ? meta.full_name.trim() : typeof meta.fullName === "string" ? meta.fullName.trim() : "";
    if (fullName) return fullName;

    const name = typeof meta.name === "string" ? meta.name.trim() : "";
    if (name) return name;

    const displayName = typeof meta.display_name === "string" ? meta.display_name.trim() : typeof meta.displayName === "string" ? meta.displayName.trim() : "";
    if (displayName) return displayName;

    return "";
  };

  const emailHandle = sessionUser.email ? sessionUser.email.split("@")[0] : "";
  const fallbackHandle =
    normalizeHandle(
      typeof metadata.handle === "string" ? metadata.handle : emailHandle,
    ) || `user_${String(sessionUser.id).slice(0, 8)}`;

  const fallbackDisplayName = pickMetadataDisplayName(metadata) || formatDisplayNameFromHandle(fallbackHandle) || "Kullanıcı";

  const metadataBirthDate = (() => {
    const fromIso = typeof metadata.birth_date === "string" ? metadata.birth_date.trim() : "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(fromIso)) return fromIso;

    const day = Number(metadata.birth_day ?? metadata.birthDay ?? 0);
    const month = Number(metadata.birth_month ?? metadata.birthMonth ?? 0);
    const year = Number(metadata.birth_year ?? metadata.birthYear ?? 0);
    if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
    const parsed = parseBirthDate({ day, month, year });
    return `${String(parsed.year).padStart(4, "0")}-${String(parsed.month).padStart(2, "0")}-${String(parsed.day).padStart(2, "0")}`;
  })();

  const select = "id, display_name, handle, photo_url, bio, birth_date, interests, match_enabled";

  const { data, error } = await supabase
    .from("karma_profiles")
    .select(select)
    .eq("id", sessionUser.id)
    .maybeSingle();

  if (error) throw error;

  const row: any =
    data ??
    {
      id: sessionUser.id,
      display_name: fallbackDisplayName,
      handle: fallbackHandle,
      photo_url: null,
      bio: null,
      birth_date: metadataBirthDate,
      interests: [],
      match_enabled: true,
    };

  const effectiveRow = {
    ...row,
    display_name: String((row as any).display_name ?? "").trim() || fallbackDisplayName,
    handle: String((row as any).handle ?? "").trim() || fallbackHandle,
    birth_date: String((row as any).birth_date ?? "").trim() || metadataBirthDate,
    photo_url:
      typeof (row as any).photo_url === "string" && String((row as any).photo_url).trim()
        ? String((row as any).photo_url).trim()
        : typeof metadata.avatar_url === "string"
          ? metadata.avatar_url
          : null,
  };

  const resolvedDisplayName = normalizeDisplayName(effectiveRow, { fallbackDisplayName });
  const birthDate = parseBirthDateFromIso((effectiveRow as any).birth_date);
  const numerology = birthDate ? computeKarmaNumerology(resolvedDisplayName, birthDate) : null;
  const destinyNumber = numerology?.destinyNumber ?? null;
  const lifePathNumber = numerology ? (numerology.lifePathMaster ?? numerology.destinyNumber) : null;
  const expressionNumber = numerology?.expressionNumber ?? null;

  return {
    id: String((effectiveRow as any).id ?? sessionUser.id),
    displayName: resolvedDisplayName,
    handle: String((effectiveRow as any).handle ?? fallbackHandle),
    photoUrl: typeof (effectiveRow as any).photo_url === "string" ? (effectiveRow as any).photo_url : null,
    bio: typeof (effectiveRow as any).bio === "string" ? (effectiveRow as any).bio : null,
    interests: parseInterests((effectiveRow as any).interests),
    matchEnabled: (effectiveRow as any).match_enabled !== false,
    destinyNumber,
    lifePathNumber,
    expressionNumber,
  };
}

export async function listSocialConnectionsInDb() {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const [followingRes, followersRes] = await Promise.all([
    supabase.from("karma_likes").select("to_user_id").eq("from_user_id", sessionUser.id),
    supabase.from("karma_likes").select("from_user_id").eq("to_user_id", sessionUser.id),
  ]);

  if (followingRes.error) throw followingRes.error;
  if (followersRes.error) throw followersRes.error;

  const followingIds = ((followingRes.data ?? []) as any[]).map((r) => String(r.to_user_id ?? "")).filter(Boolean);
  const followerIds = ((followersRes.data ?? []) as any[]).map((r) => String(r.from_user_id ?? "")).filter(Boolean);

  const followingSet = new Set(followingIds);
  const followerSet = new Set(followerIds);
  const friendIds = followingIds.filter((id) => followerSet.has(id));

  return {
    followingIds,
    followerIds,
    friendIds,
    followingCount: followingSet.size,
    followerCount: followerSet.size,
    friendCount: friendIds.length,
  };
}

export async function toggleFollowUserInDb(targetUserId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");
  if (!targetUserId || targetUserId === sessionUser.id) return { following: false };

  const supabase = getSupabaseClient();
  const existing = await supabase
    .from("karma_likes")
    .select("id")
    .eq("from_user_id", sessionUser.id)
    .eq("to_user_id", targetUserId)
    .maybeSingle();

  if (existing.error) throw existing.error;

  if ((existing.data as any)?.id) {
    const remove = await supabase.from("karma_likes").delete().eq("id", String((existing.data as any).id));
    if (remove.error) throw remove.error;
    return { following: false };
  }

  const add = await supabase.from("karma_likes").insert({ from_user_id: sessionUser.id, to_user_id: targetUserId } as any);
  if (add.error) throw add.error;
  return { following: true };
}

export async function toggleBlockUserInDb(targetUserId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");
  if (!targetUserId || targetUserId === sessionUser.id) return { blocked: false };

  const supabase = getSupabaseClient();
  const existing = await supabase
    .from("karma_blocks")
    .select("id")
    .eq("from_user_id", sessionUser.id)
    .eq("to_user_id", targetUserId)
    .maybeSingle();

  if (existing.error) throw existing.error;

  if ((existing.data as any)?.id) {
    const remove = await supabase.from("karma_blocks").delete().eq("id", String((existing.data as any).id));
    if (remove.error) throw remove.error;
    return { blocked: false };
  }

  const add = await supabase.from("karma_blocks").insert({ from_user_id: sessionUser.id, to_user_id: targetUserId } as any);
  if (add.error) throw add.error;
  return { blocked: true };
}

export async function listBlockedUsersInDb(): Promise<BlockedUserItem[]> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const blocksRes = await supabase
    .from("karma_blocks")
    .select("to_user_id, created_at")
    .eq("from_user_id", sessionUser.id)
    .order("created_at", { ascending: false })
    .limit(500);

  if (blocksRes.error) throw blocksRes.error;

  const blockRows = (blocksRes.data ?? []) as Array<{ to_user_id: string | null; created_at: string | null }>;
  const targetIds = Array.from(new Set(blockRows.map((row) => String(row.to_user_id ?? "")).filter(Boolean)));

  if (!targetIds.length) return [];

  const profilesRes = await supabase
    .from("karma_profiles")
    .select("id, display_name, handle, photo_url")
    .in("id", targetIds);

  if (profilesRes.error) throw profilesRes.error;

  const profileById = new Map<string, any>();
  for (const row of (profilesRes.data ?? []) as any[]) {
    profileById.set(String(row.id ?? ""), row);
  }

  return blockRows
    .map((row) => {
      const id = String(row.to_user_id ?? "");
      if (!id) return null;
      const profileRow = profileById.get(id);
      const handle = String(profileRow?.handle ?? "").trim();
      return {
        id,
        displayName: normalizeDisplayName(profileRow ?? { display_name: "", handle }),
        handle,
        photoUrl: typeof profileRow?.photo_url === "string" ? profileRow.photo_url : null,
        blockedAt: typeof row.created_at === "string" ? row.created_at : null,
      } satisfies BlockedUserItem;
    })
    .filter(Boolean) as BlockedUserItem[];
}

export async function unblockUserInDb(targetUserId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");
  if (!targetUserId || targetUserId === sessionUser.id) return { unblocked: false };

  const supabase = getSupabaseClient();
  const remove = await supabase.from("karma_blocks").delete().eq("from_user_id", sessionUser.id).eq("to_user_id", targetUserId);
  if (remove.error) throw remove.error;

  return { unblocked: true };
}

export async function toggleMuteUserLocally(targetUserId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");
  if (!targetUserId || targetUserId === sessionUser.id) return { muted: false };

  const map = readLocalMutes();
  const mine = new Set((map[sessionUser.id] ?? []).map((id) => String(id ?? "")).filter(Boolean));
  if (mine.has(targetUserId)) mine.delete(targetUserId);
  else mine.add(targetUserId);

  map[sessionUser.id] = Array.from(mine);
  saveLocalMutes(map);
  return { muted: mine.has(targetUserId) };
}

export async function reportUserLocally(args: { targetUserId: string; reason: string }) {
  createReport({ targetUserId: args.targetUserId, reason: args.reason });
  return { ok: true };
}

export async function listSuggestedSocialProfilesInDb(limit = 8): Promise<SocialProfileCard[]> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const [meRes, profilesRes, likesFromMeRes, likesToMeRes, blocksRes] = await Promise.all([
    supabase.from("karma_profiles").select("id, display_name, handle, birth_date, interests").eq("id", sessionUser.id).maybeSingle(),
    supabase
      .from("karma_profiles")
      .select("id, display_name, handle, photo_url, bio, birth_date, interests")
      .neq("id", sessionUser.id)
      .limit(240),
    supabase.from("karma_likes").select("to_user_id").eq("from_user_id", sessionUser.id),
    supabase.from("karma_likes").select("from_user_id").eq("to_user_id", sessionUser.id),
    supabase.from("karma_blocks").select("to_user_id").eq("from_user_id", sessionUser.id),
  ]);

  if (meRes.error) throw meRes.error;
  if (profilesRes.error) throw profilesRes.error;
  if (likesFromMeRes.error) throw likesFromMeRes.error;
  if (likesToMeRes.error) throw likesToMeRes.error;
  if (blocksRes.error) throw blocksRes.error;

  const meRow = meRes.data as any;
  const myInterests = new Set(parseInterests(meRow?.interests).map((v) => v.toLocaleLowerCase("tr-TR")));
  const myBirthDate = parseBirthDateFromIso(meRow?.birth_date);
  const myName = normalizeDisplayName(meRow);
  const myNumerology = myBirthDate ? computeKarmaNumerology(myName, myBirthDate) : null;

  const followingSet = new Set(((likesFromMeRes.data ?? []) as any[]).map((r) => String(r.to_user_id ?? "")).filter(Boolean));
  const followerSet = new Set(((likesToMeRes.data ?? []) as any[]).map((r) => String(r.from_user_id ?? "")).filter(Boolean));
  const blockedSet = new Set(((blocksRes.data ?? []) as any[]).map((r) => String(r.to_user_id ?? "")).filter(Boolean));
  const mutedMap = readLocalMutes();
  const mutedSet = new Set((mutedMap[sessionUser.id] ?? []).map((id) => String(id ?? "")).filter(Boolean));

  const scored = ((profilesRes.data ?? []) as any[])
    .map((row) => {
      const id = String(row.id ?? "");
      if (!id || blockedSet.has(id)) return null;

      const interests = parseInterests(row.interests);
      const commonInterests = interests.filter((it) => myInterests.has(it.toLocaleLowerCase("tr-TR"))).length;

      const birthDate = parseBirthDateFromIso(row.birth_date);
      const profileName = normalizeDisplayName(row);
      const numerology = birthDate ? computeKarmaNumerology(profileName, birthDate) : null;

      let matchScore = commonInterests * 14;
      if (myNumerology && numerology) {
        if (myNumerology.destinyNumber === numerology.destinyNumber) matchScore += 35;
        if (myNumerology.heartDesireNumber === numerology.heartDesireNumber) matchScore += 20;
        if (myNumerology.personalityNumber === numerology.personalityNumber) matchScore += 16;
      }

      if (followerSet.has(id)) matchScore += 8;
      if (followingSet.has(id)) matchScore -= 5;

      return {
        id,
        displayName: profileName,
        handle: String(row.handle ?? "").trim(),
        photoUrl: typeof row.photo_url === "string" ? row.photo_url : null,
        bio: typeof row.bio === "string" ? row.bio : null,
        interests,
        destinyNumber: numerology?.destinyNumber ?? null,
        lifePathNumber: numerology ? (numerology.lifePathMaster ?? numerology.destinyNumber) : null,
        expressionNumber: numerology?.expressionNumber ?? null,
        matchScore: Math.max(0, Math.min(100, Math.round(matchScore))),
        commonInterests,
        followedByMe: followingSet.has(id),
        followsMe: followerSet.has(id),
        isFriend: followingSet.has(id) && followerSet.has(id),
        isBlocked: blockedSet.has(id),
        isMuted: mutedSet.has(id),
      } satisfies SocialProfileCard;
    })
    .filter(Boolean) as SocialProfileCard[];

  return scored
    .sort((a, b) => {
      if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
      if (b.commonInterests !== a.commonInterests) return b.commonInterests - a.commonInterests;
      return a.displayName.localeCompare(b.displayName, "tr-TR");
    })
    .slice(0, Math.max(1, Math.min(16, Math.trunc(Number(limit) || 8))));
}

export async function listRecentSocialActivitiesInDb(limit = 8): Promise<SocialActivityItem[]> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const completionsRes = await supabase
    .from("circle_task_completions")
    .select("id, user_id, circle_id, task_id, created_at, date_key")
    .order("created_at", { ascending: false })
    .limit(Math.max(4, Math.min(80, Math.trunc(Number(limit) || 8) * 3)));

  if (completionsRes.error) {
    return [];
  }

  const rawRows = (completionsRes.data ?? []) as any[];
  if (!rawRows.length) return [];

  // Aynı kullanıcı + görev + gün için birden fazla completion oluştuysa,
  // akışta sadece en yeni satırı göster.
  const dedupedRowsByDailyTask = new Map<string, any>();
  for (const row of rawRows) {
    const userId = String(row.user_id ?? "");
    const taskId = String(row.task_id ?? "");
    const dateKey =
      typeof row.date_key === "string" && row.date_key.trim()
        ? row.date_key.trim()
        : typeof row.created_at === "string"
          ? row.created_at.slice(0, 10)
          : "";
    const circleId = String(row.circle_id ?? "");
    const key = `${userId}::${circleId}::${taskId}::${dateKey}`;
    if (!dedupedRowsByDailyTask.has(key)) {
      dedupedRowsByDailyTask.set(key, row);
    }
  }

  const rows = Array.from(dedupedRowsByDailyTask.values());

  const circleIds = Array.from(new Set(rows.map((r) => String(r.circle_id ?? "")).filter(Boolean)));
  const taskIds = Array.from(new Set(rows.map((r) => String(r.task_id ?? "")).filter(Boolean)));
  const userIds = Array.from(new Set(rows.map((r) => String(r.user_id ?? "")).filter(Boolean)));

  const [circlesRes, tasksRes, profilesRes] = await Promise.all([
    circleIds.length ? supabase.from("circles").select("id, name").in("id", circleIds) : Promise.resolve({ data: [], error: null } as any),
    taskIds.length ? supabase.from("circle_tasks").select("id, title, points, kind").in("id", taskIds) : Promise.resolve({ data: [], error: null } as any),
    userIds.length
      ? supabase.from("karma_profiles").select("id, display_name, handle, photo_url").in("id", userIds)
      : Promise.resolve({ data: [], error: null } as any),
  ]);

  const circleNameById = new Map<string, string>();
  for (const row of (circlesRes.data ?? []) as any[]) {
    circleNameById.set(String(row.id ?? ""), String(row.name ?? "Çember"));
  }

  const taskTitleById = new Map<string, string>();
  const taskPointsById = new Map<string, number | null>();
  const taskKindById = new Map<string, SocialActivityKind>();
  const profileByUserId = new Map<string, any>();
  for (const row of (tasksRes.data ?? []) as any[]) {
    const id = String(row.id ?? "");
    taskTitleById.set(id, String(row.title ?? "Görev"));
    const points = Number(row.points);
    taskPointsById.set(id, Number.isFinite(points) ? Math.max(0, Math.min(100, Math.round(points))) : null);
    const rawKind = String(row.kind ?? "").toLocaleLowerCase("tr-TR");
    const kind: SocialActivityKind = rawKind === "meditasyon" || rawKind === "nefes" || rawKind === "diyet" || rawKind === "rituel" || rawKind === "sohbet" ? rawKind : "ozel";
    taskKindById.set(id, kind);
  }

  for (const row of (profilesRes.data ?? []) as any[]) {
    profileByUserId.set(String(row.id ?? ""), row);
  }

  const streakByCompletionId = new Map<string, number>();
  const groupedByUserCircleTask = new Map<string, string[]>();
  for (const row of rows) {
    const key = `${String(row.user_id ?? "")}::${String(row.circle_id ?? "")}::${String(row.task_id ?? "")}`;
    if (!groupedByUserCircleTask.has(key)) groupedByUserCircleTask.set(key, []);

    const dateKey =
      typeof row.date_key === "string" && row.date_key.trim()
        ? row.date_key.trim()
        : typeof row.created_at === "string"
          ? row.created_at.slice(0, 10)
          : "";

    groupedByUserCircleTask.get(key)?.push(dateKey);
  }

  for (const row of rows) {
    const key = `${String(row.user_id ?? "")}::${String(row.circle_id ?? "")}::${String(row.task_id ?? "")}`;
    const dateKeys = groupedByUserCircleTask.get(key) ?? [];
    const uniqueDates = Array.from(new Set(dateKeys.filter(Boolean))).sort((a, b) => String(b).localeCompare(String(a)));

    const currentDate =
      typeof row.date_key === "string" && row.date_key.trim()
        ? row.date_key.trim()
        : typeof row.created_at === "string"
          ? row.created_at.slice(0, 10)
          : "";
    const startIndex = uniqueDates.indexOf(currentDate);
    if (startIndex < 0) {
      streakByCompletionId.set(String(row.id ?? ""), 1);
      continue;
    }

    let streak = 1;
    for (let i = startIndex; i < uniqueDates.length - 1; i += 1) {
      const current = new Date(`${uniqueDates[i]}T00:00:00`);
      const next = new Date(`${uniqueDates[i + 1]}T00:00:00`);
      const diffDays = Math.round((current.getTime() - next.getTime()) / 86_400_000);
      if (diffDays === 1) streak += 1;
      else break;
    }

    streakByCompletionId.set(String(row.id ?? ""), Math.max(1, streak));
  }

  return rows.slice(0, Math.max(1, Math.min(20, limit))).map((row) => {
    const circleId = String(row.circle_id ?? "");
    const taskId = String(row.task_id ?? "");
    const dateKey = typeof row.date_key === "string" ? row.date_key : "";

    const completionId = String(row.id ?? makeId("act"));

    const authorUserId = String(row.user_id ?? "");
    const authorProfile = profileByUserId.get(authorUserId);

    return {
      id: completionId,
      title: taskTitleById.get(taskId) || "Görev tamamlandı",
      subtitle: circleNameById.get(circleId) || "Çember etkinliği",
      createdAt: typeof row.created_at === "string" ? row.created_at : `${dateKey}T00:00:00.000Z`,
      taskKind: taskKindById.get(taskId) ?? "ozel",
      taskPoints: taskPointsById.get(taskId) ?? null,
      streakDays: streakByCompletionId.get(completionId) ?? 1,
      authorUserId,
      authorName: normalizeDisplayName(authorProfile),
      authorHandle: String(authorProfile?.handle ?? "user"),
      authorPhotoUrl: typeof authorProfile?.photo_url === "string" ? authorProfile.photo_url : null,
    } satisfies SocialActivityItem;
  });
}

export async function updateDailySocialStatus(args: { text: string; visibility: SocialVisibility }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const map = readLocalJson<Record<string, { text: string; visibility: SocialVisibility; updatedAt: string }>>(SOCIAL_STATUS_KEY, {});
  map[sessionUser.id] = {
    text: String(args.text ?? "").trim().slice(0, 180),
    visibility: args.visibility,
    updatedAt: getNowIso(),
  };
  writeLocalJson(SOCIAL_STATUS_KEY, map);

  return map[sessionUser.id];
}

export async function getMyDailySocialStatus() {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");
  const map = readLocalJson<Record<string, { text: string; visibility: SocialVisibility; updatedAt: string }>>(SOCIAL_STATUS_KEY, {});
  return map[sessionUser.id] ?? null;
}

export async function createSocialPost(args: {
  body: string;
  media?: Array<{ url: string; type?: "image" | "video" }>;
  location?: string | null;
  taggedHandles?: string[];
  postType?: "standard" | "affirmation";
  affirmationStyle?: "violet" | "rose";
}) {
  const json = await socialApiRequest<{ item?: LocalPost }>("/api/numeroloji/social/posts", {
    method: "POST",
    body: JSON.stringify({
      body: args.body,
      media: args.media,
      location: args.location,
      taggedHandles: args.taggedHandles,
      postType: args.postType,
      affirmationStyle: args.affirmationStyle,
    }),
  });

  const item = migrateLegacyPosts([json.item])[0] ?? null;
  if (!item) throw new Error("Gönderi kaydedilemedi");
  return item;
}

export async function repostSocialPost(args: { postId: string; caption?: string }) {
  return await socialApiRequest<{ reposted?: boolean }>(`/api/numeroloji/social/posts/${encodeURIComponent(args.postId)}/repost`, {
    method: "POST",
    body: JSON.stringify({ caption: args.caption ?? "" }),
  });
}

export async function togglePinSocialPost(postId: string) {
  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(postId)}/pin`, {
    method: "POST",
  });
}

export async function updateSocialPost(args: { postId: string; body: string }) {
  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(args.postId)}/update`, {
    method: "POST",
    body: JSON.stringify({ body: args.body }),
  });
}

export async function deleteSocialPost(postId: string) {
  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(postId)}/delete`, {
    method: "POST",
  });
}

export async function deleteSocialActivity(activityId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const id = String(activityId ?? "").trim();
  if (!id) throw new Error("Aktivite bulunamadı");

  const supabase = getSupabaseClient();
  const remove = await supabase.from("circle_task_completions").delete().eq("id", id).eq("user_id", sessionUser.id);
  if (remove.error) throw remove.error;
}

export async function toggleLikeSocialPost(postId: string) {
  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(postId)}/like`, {
    method: "POST",
  });
}

export async function toggleSaveSocialPost(postId: string) {
  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(postId)}/save`, {
    method: "POST",
  });
}

export async function toggleSocialPostReaction(args: { postId: string; reaction: string }) {
  if (String(args.reaction ?? "").toLocaleLowerCase("tr-TR") === "begeni") {
    await toggleLikeSocialPost(args.postId);
    return;
  }

  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(args.postId)}/reaction`, {
    method: "POST",
    body: JSON.stringify({ reaction: args.reaction }),
  });
}

export async function addSocialPostComment(args: { postId: string; body: string }) {
  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(args.postId)}/comment`, {
    method: "POST",
    body: JSON.stringify({ body: args.body }),
  });
}

export async function updateSocialPostComment(args: { postId: string; commentId: string; body: string }) {
  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(args.postId)}/comment/${encodeURIComponent(args.commentId)}/update`, {
    method: "POST",
    body: JSON.stringify({ body: args.body }),
  });
}

export async function deleteSocialPostComment(args: { postId: string; commentId: string }) {
  await socialApiRequest(`/api/numeroloji/social/posts/${encodeURIComponent(args.postId)}/comment/${encodeURIComponent(args.commentId)}/delete`, {
    method: "POST",
  });
}

function mapPostToFeedPost(args: { post: LocalPost; profileById: Map<string, any>; viewerId: string }): SocialFeedPost {
  const { post, profileById, viewerId } = args;
  const author = profileById.get(post.authorUserId);

  const viewCount = Math.max(1, post.seenUserIds.length);
  const interactionSet = new Set([...post.likedUserIds, ...post.savedUserIds]);
  const interactionCount = interactionSet.size;
  const collectiveEnergyPercent = Math.max(0, Math.min(100, Math.round((interactionCount / viewCount) * 100)));

  return {
    id: post.id,
    authorUserId: post.authorUserId,
    authorName: normalizeDisplayName(author),
    authorHandle: String(author?.handle ?? "user"),
    authorPhotoUrl: typeof author?.photo_url === "string" ? author.photo_url : null,
    body: post.body,
    tags: post.tags,
    location: post.location,
    taggedHandles: post.taggedHandles,
    media: post.media,
    createdAt: post.createdAt,
    pinned: Boolean(post.pinned),
    isRepost: Boolean(post.isRepost),
    repostOfPostId: post.repostOfPostId,
    postType: post.postType,
    affirmationStyle: post.affirmationStyle,
    likeCount: post.likedUserIds.length,
    commentCount: post.comments.length,
    saveCount: post.savedUserIds.length,
    repostCount: post.repostedUserIds.length,
    viewCount,
    interactionCount,
    collectiveEnergyPercent,
    likedByMe: post.likedUserIds.includes(viewerId),
    savedByMe: post.savedUserIds.includes(viewerId),
    repostedByMe: post.repostedUserIds.includes(viewerId),
    reactions: post.reactions ?? {},
    comments: (post.comments ?? []).map((comment) => {
      const row = profileById.get(comment.userId);
      return {
        id: comment.id,
        userId: comment.userId,
        userName: normalizeDisplayName(row),
        body: comment.body,
        createdAt: comment.createdAt,
      };
    }),
  } satisfies SocialFeedPost;
}

export async function listSocialFeedPosts(limit = 20): Promise<SocialFeedPost[]> {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const cappedLimit = Math.max(1, Math.min(120, Math.trunc(Number(limit) || 20)));
  const json = await socialApiRequest<{ items?: unknown[] }>(`/api/numeroloji/social/posts?limit=${cappedLimit}`, {
    method: "GET",
  });

  const trackedPosts = migrateLegacyPosts(Array.isArray(json.items) ? json.items : []);
  if (!trackedPosts.length) return [];

  const userIds = Array.from(
    new Set(
      trackedPosts
        .flatMap((post) => [post.authorUserId, ...post.comments.map((comment) => comment.userId)])
        .map((id) => String(id ?? ""))
        .filter(Boolean),
    ),
  );

  const supabase = getSupabaseClient();
  const profilesRes = await supabase.from("karma_profiles").select("id, display_name, handle, photo_url").in("id", userIds);
  if (profilesRes.error) throw profilesRes.error;

  const profileById = new Map<string, any>();
  for (const row of (profilesRes.data ?? []) as any[]) {
    profileById.set(String(row.id ?? ""), row);
  }

  return trackedPosts
    .slice()
    .sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return String(b.createdAt).localeCompare(String(a.createdAt));
    })
    .slice(0, cappedLimit)
    .map((post) => mapPostToFeedPost({ post, profileById, viewerId: sessionUser.id }));
}

export async function listProfilePostsByTab(args: { tab: SocialPostTab; limit?: number }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const all = await listSocialFeedPosts(200);
  const myHandle = (() => {
    const mine = all.find((post) => post.authorUserId === sessionUser.id);
    return (mine?.authorHandle ?? "").toLocaleLowerCase("tr-TR");
  })();

  const filtered =
    args.tab === "posts"
      ? all.filter((post) => post.authorUserId === sessionUser.id && !post.isRepost)
      : args.tab === "reposts"
        ? all.filter((post) => post.isRepost && post.authorUserId === sessionUser.id)
        : args.tab === "tagged"
          ? all.filter((post) => post.taggedHandles.includes(myHandle))
          : all.filter((post) => post.savedByMe);

  return filtered.slice(0, Math.max(1, Math.min(120, Math.trunc(Number(args.limit) || 60))));
}

export async function listExploreMediaPosts(limit = 36) {
  const all = await listSocialFeedPosts(240);
  return all
    .filter((post) => post.media.length > 0)
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
    .slice(0, Math.max(1, Math.min(120, Math.trunc(Number(limit) || 36))));
}

export async function sendWaveToUser(args: { targetUserId: string }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");
  if (!args.targetUserId || args.targetUserId === sessionUser.id) return { ok: true };

  const body = "Selam, profilini gördüm. Uygun olursan bağlantı kurmak isterim. #selam";
  await createSocialPost({ body });
  return { ok: true };
}

export type DirectMessageItem = {
  id: string;
  fromUserId: string;
  toUserId: string;
  text: string;
  createdAt: string;
};

export async function listDirectMessagesBetweenUsers(args: { peerUserId: string; limit?: number }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const peerUserId = String(args.peerUserId ?? "").trim();
  if (!peerUserId || peerUserId === sessionUser.id) return [] as DirectMessageItem[];

  const limit = Math.max(1, Math.min(500, Math.trunc(Number(args.limit) || 200)));
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from("karma_direct_messages")
    .select("id, from_user_id, to_user_id, body, created_at")
    .or(`and(from_user_id.eq.${sessionUser.id},to_user_id.eq.${peerUserId}),and(from_user_id.eq.${peerUserId},to_user_id.eq.${sessionUser.id})`)
    .order("created_at", { ascending: true })
    .limit(limit);

  if (error) throw error;

  return ((data ?? []) as any[]).map((row) => ({
    id: String(row.id),
    fromUserId: String(row.from_user_id),
    toUserId: String(row.to_user_id),
    text: String(row.body ?? ""),
    createdAt: String(row.created_at),
  })) as DirectMessageItem[];
}

export async function sendDirectMessageToUser(args: { toUserId: string; body: string }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const toUserId = String(args.toUserId ?? "").trim();
  const body = String(args.body ?? "").trim();

  if (!toUserId || toUserId === sessionUser.id) throw new Error("Geçersiz hedef kullanıcı");
  if (!body) throw new Error("Mesaj boş olamaz");

  const response = await socialApiRequest<{
    item?: { id?: string; fromUserId?: string; toUserId?: string; text?: string; createdAt?: string };
  }>("/api/numeroloji/direct-messages/send", {
    method: "POST",
    body: JSON.stringify({ toUserId, body }),
  });

  const item = response.item;
  if (!item?.id) throw new Error("Mesaj gönderilemedi");

  return {
    id: String(item.id),
    fromUserId: String(item.fromUserId ?? sessionUser.id),
    toUserId: String(item.toUserId ?? toUserId),
    text: String(item.text ?? body),
    createdAt: String(item.createdAt ?? new Date().toISOString()),
  } as DirectMessageItem;
}

export async function deleteDirectMessageById(messageId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const id = String(messageId ?? "").trim();
  if (!id) throw new Error("Mesaj bulunamadı");

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from("karma_direct_messages")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

export async function updateDirectMessageById(args: { messageId: string; body: string }) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const id = String(args.messageId ?? "").trim();
  const body = String(args.body ?? "").trim();
  if (!id) throw new Error("Mesaj bulunamadı");
  if (!body) throw new Error("Mesaj boş olamaz");

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("karma_direct_messages")
    .update({ body })
    .eq("id", id)
    .eq("from_user_id", sessionUser.id)
    .select("id, from_user_id, to_user_id, body, created_at")
    .single();

  if (error) throw error;

  return {
    id: String((data as any).id),
    fromUserId: String((data as any).from_user_id),
    toUserId: String((data as any).to_user_id),
    text: String((data as any).body ?? ""),
    createdAt: String((data as any).created_at),
  } as DirectMessageItem;
}

export async function deleteDirectConversationWithUser(peerUserId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const peerId = String(peerUserId ?? "").trim();
  if (!peerId || peerId === sessionUser.id) return;

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from("karma_direct_messages")
    .delete()
    .or(`and(from_user_id.eq.${sessionUser.id},to_user_id.eq.${peerId}),and(from_user_id.eq.${peerId},to_user_id.eq.${sessionUser.id})`);

  if (error) throw error;
}

export async function blockUserAndDeleteConversation(targetUserId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const blockResult = await toggleBlockUserInDb(targetUserId);
  if (blockResult.blocked) {
    const supabase = getSupabaseClient();
    const { error: unlikeError } = await supabase
      .from("karma_likes")
      .delete()
      .eq("from_user_id", sessionUser.id)
      .eq("to_user_id", targetUserId);

    if (unlikeError) throw unlikeError;
    await deleteDirectConversationWithUser(targetUserId);
  }
  return blockResult;
}

export async function listDirectMessageConversationSummaries() {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok");

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("karma_direct_messages")
    .select("id, from_user_id, to_user_id, body, created_at")
    .or(`from_user_id.eq.${sessionUser.id},to_user_id.eq.${sessionUser.id}`)
    .order("created_at", { ascending: false })
    .limit(1000);

  if (error) throw error;

  const byPeer = new Map<string, DirectMessageItem>();
  for (const row of (data ?? []) as any[]) {
    const fromUserId = String(row.from_user_id ?? "");
    const toUserId = String(row.to_user_id ?? "");
    const peerId = fromUserId === sessionUser.id ? toUserId : fromUserId;
    if (!peerId || byPeer.has(peerId)) continue;

    byPeer.set(peerId, {
      id: String(row.id),
      fromUserId,
      toUserId,
      text: String(row.body ?? ""),
      createdAt: String(row.created_at),
    });
  }

  return byPeer;
}
